<script setup>
import { useRouter } from 'vue-router'
import NavbarThemeSwitcher from '@/layouts/components/NavbarThemeSwitcher.vue'
import { themeConfig } from '@themeConfig'
import { useConfigStore } from '@core/stores/config'

const router = useRouter()
const btnText = ref('Login')
const configStore = useConfigStore()

// Current year for footer
const currentYear = computed(() => new Date().getFullYear())

const goToLoginPage = () => {
  // Use the router's push method to navigate to the login page
  let token = useCookie("token").value;
  if(token){
    router.push('/dashboard')
  }else{
    router.push('/login')
  }
}
const getToken = async () => {
    let token = useCookie("token").value;
    if(token){
      btnText.value = 'Dashboard'
    }

}

onMounted(getToken);

definePage({ meta: { layout: 'blank' } });
</script>

<template>
  <div>
    <VRow>
    <VCol cols="4" md="4"> <!-- On medium screens and above, take 9 columns, on smaller screens take all 12 columns -->
      <img 
        v-if="configStore.theme == 'light'" 
        src="/images/Kingswood_RGB_450px.png" 
        alt="Kingswood Connect Logo" 
        class="mx-5 my-4"
        style="height: 50px;"
      >
      <img 
        v-else-if="configStore.theme == 'dark'" 
        src="/images/Kingswood_NEG_RGB_450px.png" 
        alt="Kingswood Connect Dark Logo" 
        class="mx-5 my-4"
        style="height: 50px;"
      >
    </VCol>
    <VCol cols="8" md="8"> <!-- On medium screens and above, take 3 columns, on smaller screens take all 12 columns -->
      <VBtn
        color="primary"
        variant="tonal"
        class="ma-5 float-end"
        @click="goToLoginPage"
      >
      {{ btnText }}
      </VBtn>
      <NavbarThemeSwitcher class="my-5 float-end" />
    </VCol>
  </VRow>
   <!-- Hero Section -->
    <section class="hero-section">
      <!-- Background Elements -->
      <div class="background-gradient"></div>
      <div class="decorative-blob-1"></div>
      <div class="decorative-blob-2"></div>
      
      <!-- Content -->
      <VContainer class="hero-container">
        <div class="content-wrapper">
          <!-- Logo Area -->
          <div class="logo-area">
            <h2 class="logo-text">KINGSWOOD CONNECT</h2>
          </div>
          
          <!-- Main Headline -->
          <h1 class="main-headline">
            Execute, Track, & <br> Manage with <br>
            <span class="highlight">Kingswood Connect</span>
          </h1>
          
          <!-- Subtext -->
          <p class="subtext">
            Kingswood Connect is a custom software built for the firm to help reps execute workflows, 
            manage client relationships, and access firm support.
          </p>
          
          <!-- CTA Button -->
          <VBtn
            class="cta-button"
            size="large"
            @click="goToLoginPage"
          >
            {{ btnText }}
          </VBtn>
        </div>
      </VContainer>
    </section>

    <!-- Features Section -->
    <section class="features">
      <VContainer class="features-container">
        <div class="features-grid-new">
          <div class="feature-card-new">
            <div class="feature-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3>Workflow Execution</h3>
            <p>Execute complex workflows efficiently with streamlined processes designed for financial professionals.</p>
          </div>

          <div class="feature-card-new">
            <div class="feature-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h3>Client Management</h3>
            <p>Manage client relationships with powerful tools that keep all your important information organized and accessible.</p>
          </div>

          <div class="feature-card-new">
            <div class="feature-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
              </svg>
            </div>
            <h3>Firm Support Access</h3>
            <p>Get instant access to firm support and resources whenever you need assistance, ensuring you're never alone.</p>
          </div>
        </div>
      </VContainer>
    </section>

    <!-- Benefits Section -->
    <section class="benefits">
      <VContainer>
        <div class="benefit-row">
          <div class="benefit-content">
            <h2>Streamlined Operations</h2>
            <p>Transform how you work with intelligent automation and intuitive workflows designed specifically for your firm's needs.</p>
            <ul class="benefit-list">
              <li>
                <span class="check-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </span>
                <span>Automated workflow tracking</span>
              </li>
              <li>
                <span class="check-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </span>
                <span>Real-time progress monitoring</span>
              </li>
              <li>
                <span class="check-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </span>
                <span>Seamless task management</span>
              </li>
            </ul>
          </div>
          <div>
            <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=800&fit=crop" alt="Modern workspace" class="benefit-image">
          </div>
        </div>

        <div class="benefit-row">
          <div style="order: 1;">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=800&fit=crop" alt="Team collaboration" class="benefit-image">
          </div>
          <div class="benefit-content" style="order: 1;">
            <h2>Enhanced Collaboration</h2>
            <p>Keep your entire team connected and aligned with tools that facilitate communication and information sharing.</p>
            <ul class="benefit-list">
              <li>
                <span class="check-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </span>
                <span>Centralized client information</span>
              </li>
              <li>
                <span class="check-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </span>
                <span>Team collaboration features</span>
              </li>
              <li>
                <span class="check-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </span>
                <span>Integrated communication tools</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="benefit-row mobile-app-section">
          <div class="benefit-content mobile-content">
            <h2>Kingswood Connect Mobile App — Coming Soon</h2>
            <p>Manage clients, track workflows, and access firm support anywhere with the Kingswood Connect mobile app.</p>
            
            <!-- App Store Buttons -->
            <div class="app-store-buttons">
              <div class="store-button coming-soon">
                <svg viewBox="0 0 24 24" class="store-icon">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <div class="store-text">
                  <div class="store-subtitle">Download on the</div>
                  <div class="store-title">App Store</div>
                </div>
              </div>
              
              <div class="store-button coming-soon">
                <svg viewBox="0 0 24 24" class="store-icon">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div class="store-text">
                  <div class="store-subtitle">Get it on</div>
                  <div class="store-title">Google Play</div>
                </div>
              </div>
            </div>
            
            <!-- Coming Soon Badge -->
            <div class="coming-soon-badge">
              <span>Coming Soon</span>
            </div>
          </div>
          
          <div class="mobile-mockup-container">
            <div class="phone-mockup">
              <div class="phone-frame">
                <div class="phone-screen">
                  <div class="app-interface">
                    <!-- Status Bar -->
                    <div class="status-bar">
                      <span class="time">9:41</span>
                      <div class="status-icons">
                        <div class="signal"></div>
                        <div class="wifi"></div>
                        <div class="battery"></div>
                      </div>
                    </div>
                    
                    <!-- Centered Logo -->
                    <div class="logo-center">
                      <img 
                        v-if="configStore.theme == 'light'" 
                        src="/images/Kingswood_RGB_450px.png" 
                        alt="Kingswood Connect Logo" 
                        class="mobile-logo"
                      >
                      <img 
                        v-else-if="configStore.theme == 'dark'" 
                        src="/images/Kingswood_NEG_RGB_450px.png" 
                        alt="Kingswood Connect Dark Logo" 
                        class="mobile-logo"
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VContainer>
    </section>

    <!-- Stats Section -->
    <section class="stats">
      <VContainer>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">500+</div>
            <div class="stat-label">Reps</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">99.9%</div>
            <div class="stat-label">Uptime</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">24/7</div>
            <div class="stat-label">Support</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">Enterprise</div>
            <div class="stat-label">Security</div>
          </div>
        </div>
      </VContainer>
    </section>

    <!-- Footer -->
    <VFooter class="custom-footer">
      <VContainer>
        <VRow align="center" justify="space-between">
          <VCol cols="12" md="6">
            <div class="footer-brand">
              <h3>Kingswood Connect</h3>
              <p class="footer-tagline">Custom Software for Financial Excellence</p>
            </div>
          </VCol>
          <VCol cols="12" md="6" class="text-md-right">
            <div class="footer-copyright">
              <p>&copy; {{ currentYear }} Kingswood Connect. All rights reserved.</p>
              <p style="margin-top: 4px;">Empowering financial professionals nationwide</p>
            </div>
          </VCol>
        </VRow>
      </VContainer>
    </VFooter>  
  </div>
</template>

<style scoped>
/* Hero Section */
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* Background gradient adapted for dark/light mode */
.background-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgb(var(--v-theme-surface)) 0%, rgba(var(--v-theme-primary), 0.05) 50%, rgb(var(--v-theme-surface)) 100%);
  opacity: 0.5;
}

.decorative-blob-1 {
  position: absolute;
  top: 80px;
  left: 40px;
  width: 288px;
  height: 288px;
  background: rgba(var(--v-theme-primary), 0.1);
  border-radius: 50%;
  filter: blur(60px);
}

.decorative-blob-2 {
  position: absolute;
  bottom: 80px;
  right: 40px;
  width: 384px;
  height: 384px;
  background: rgba(var(--v-theme-primary), 0.15);
  border-radius: 50%;
  filter: blur(60px);
}

.hero-container {
  position: relative;
  z-index: 10;
  max-width: 1200px;
  margin: 0 auto;
  padding: 50px 24px;
  text-align: center;
}

.content-wrapper {
  max-width: 896px;
  margin: 0 auto;
  animation: fadeIn 1s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.logo-area {
  margin-bottom: 28px;
}

.logo-text {
  font-size: 1.875rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  letter-spacing: 0.1em;
}

.main-headline {
  font-size: 3.75rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.2;
  margin-bottom: 28px;
}

.highlight {
  color: rgb(var(--v-theme-primary));
}

.subtext {
  font-size: 1.25rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  max-width: 672px;
  margin: 0 auto 44px;
  line-height: 1.8;
}

.cta-button {
  display: inline-block;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgba(var(--v-theme-primary), 0.8)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
  padding: 10px 48px;
  font-size: 1.125rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 40px -10px rgba(var(--v-theme-primary), 0.3);
}

.cta-button:hover {
  transform: scale(1.05);
  box-shadow: 0 20px 60px -10px rgba(var(--v-theme-primary), 0.4);
}

.features-grid {
  margin-top: 64px;
}

.feature-card {
  background: rgb(var(--v-theme-surface)) !important;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-outline), 0.2);
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 40px -10px rgba(var(--v-theme-primary), 0.3);
}

.feature-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 8px;
}

.feature-description {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

/* .custom-footer {
  background-color: rgba(var(--v-theme-surface-variant), 0.8) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  padding: 32px 24px;
  margin-top: auto;
} */

.footer-brand h3 {
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 4px;
  color: rgb(var(--v-theme-on-surface));
}

.footer-tagline {
  font-size: 0.875rem;
  opacity: 0.8;
  color: rgba(var(--v-theme-on-surface), 0.8);
}

.footer-copyright {
  font-size: 0.875rem;
  opacity: 0.8;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.8);
}

.footer-copyright p {
  color: rgba(var(--v-theme-on-surface), 0.8);
}

/* Responsive Design */
@media (min-width: 768px) {
  .logo-text {
    font-size: 1.875rem;
  }

  .main-headline {
    font-size: 4.5rem;
  }

  .subtext {
    font-size: 1.25rem;
  }

  .footer-copyright {
    text-align: right;
  }
}

@media (min-width: 1024px) {
  .main-headline {
    font-size: 4.5rem;
  }
}

@media (max-width: 767px) {
  .main-headline {
    font-size: 2.25rem;
  }

  .logo-text {
    font-size: 1.5rem;
  }

  .cta-button {
    padding: 10px 40px;
  }
}

/* Features Section */
.features {
  padding: 4rem 1.5rem;
  background: rgba(var(--v-theme-primary), 0.02);
}

.features-container {
  max-width: 1280px;
  margin: 0 auto;
}

.features-grid-new {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.feature-card-new {
  background: rgb(var(--v-theme-surface)) !important;
  border-radius: 0.75rem;
  padding: 2rem;
  transition: all 0.2s;
  border: 1px solid rgba(var(--v-theme-outline), 0.2);
}

.feature-card-new:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 30px rgba(var(--v-theme-primary), 0.1);
}

.feature-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgba(var(--v-theme-primary), 0.8));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.feature-icon svg {
  width: 32px;
  height: 32px;
  color: rgb(var(--v-theme-on-primary));
}

.feature-card-new h3 {
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

.feature-card-new p {
  color: rgba(var(--v-theme-on-surface), 0.7);
  line-height: 1.7;
}

/* Benefits Section */
.benefits {
  padding: 4rem 1.5rem;
}

.benefit-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 6rem;
}

.benefit-row:last-child {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .benefit-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

.benefit-content h2 {
  font-size: clamp(1.875rem, 3vw, 2.25rem);
  margin-bottom: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

.benefit-content p {
  font-size: 1.125rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  margin-bottom: 1.5rem;
  line-height: 1.7;
}

.benefit-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefit-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: rgb(var(--v-theme-on-surface));
}

.check-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.check-icon svg {
  width: 12px;
  height: 12px;
  color: rgb(var(--v-theme-primary));
}

.benefit-image {
  border-radius: 0.5rem;
  width: 100%;
  height: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

/* Stats Section */
.stats {
  padding: 4rem 1.5rem;
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.05), rgba(var(--v-theme-primary), 0.02), transparent);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  background: rgb(var(--v-theme-surface));
  border-radius: 0.75rem;
  padding: 2rem 1.5rem;
  text-align: center;
  border: 1px solid rgba(var(--v-theme-outline), 0.2);
}

.stat-value {
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

/* Mobile App Section */
.mobile-app-section {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.03), rgba(var(--v-theme-primary), 0.01));
  border-radius: 1rem;
  padding: 3rem 2rem;
  margin: 2rem 0;
}

.mobile-content h2 {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgba(var(--v-theme-primary), 0.8));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
}

.app-store-buttons {
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;
}

.store-button {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: rgba(var(--v-theme-on-surface), 0.1);
  border: 2px solid rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  min-width: 160px;
  cursor: not-allowed;
}

.store-button.coming-soon {
  opacity: 0.6;
  filter: grayscale(1);
}

.store-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(var(--v-theme-primary), 0.15);
}

.store-icon {
  width: 24px;
  height: 24px;
  fill: rgb(var(--v-theme-on-surface));
}

.store-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.store-subtitle {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  line-height: 1;
}

.store-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.2;
}

.coming-soon-badge {
  display: inline-block;
  margin-top: 1rem;
}

.coming-soon-badge span {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgba(var(--v-theme-primary), 0.8));
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(var(--v-theme-primary), 0.3);
}

.mobile-mockup-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.phone-mockup {
  animation: phoneFloat 3s ease-in-out infinite;
}

@keyframes phoneFloat {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.phone-frame {
  width: 280px;
  height: 560px;
  background: linear-gradient(145deg, #1a1a1a, #2d2d2d);
  border-radius: 2.5rem;
  padding: 1rem;
  box-shadow: 
    0 0 0 3px #333,
    0 20px 60px rgba(0, 0, 0, 0.4),
    inset 0 1px 1px rgba(255, 255, 255, 0.1);
  position: relative;
}

.phone-frame::before {
  content: '';
  position: absolute;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 4px;
  background: #444;
  border-radius: 2px;
}

.phone-screen {
  width: 100%;
  height: 100%;
  background: rgb(var(--v-theme-surface));
  border-radius: 1.8rem;
  overflow: hidden;
  position: relative;
}

.app-interface {
  padding: 1rem;
  height: 100%;
  background: rgb(var(--v-theme-surface));
  display: flex;
  flex-direction: column;
}

.status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

.time {
  color: rgb(var(--v-theme-on-surface));
}

.status-icons {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.signal, .wifi, .battery {
  width: 16px;
  height: 10px;
  background: rgb(var(--v-theme-on-surface));
  border-radius: 2px;
}

.signal {
  background: linear-gradient(to right, 
    rgb(var(--v-theme-primary)) 0% 30%, 
    rgba(var(--v-theme-on-surface), 0.3) 30% 100%);
}

.wifi {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: radial-gradient(circle, rgb(var(--v-theme-primary)) 0% 60%, transparent 60%);
}

.battery {
  width: 20px;
  background: linear-gradient(to right, 
    #4ade80 0% 80%, 
    rgba(var(--v-theme-on-surface), 0.3) 80% 100%);
}

.logo-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}

.mobile-logo {
  max-width: 80%;
  height: auto;
  max-height: 120px;
  object-fit: contain;
  filter: drop-shadow(0 4px 20px rgba(var(--v-theme-primary), 0.2));
}

@media (max-width: 768px) {
  .mobile-app-section {
    padding: 2rem 1rem;
  }
  
  .app-store-buttons {
    justify-content: center;
  }
  
  .phone-frame {
    width: 240px;
    height: 480px;
  }
  
  .mobile-app-section .benefit-row {
    grid-template-columns: 1fr;
    text-align: center;
  }
  
  .mobile-mockup-container {
    order: -1;
    margin-bottom: 2rem;
  }
}

@media (max-width: 480px) {
  .store-button {
    min-width: 140px;
    padding: 0.6rem 1rem;
  }
  
  .store-title {
    font-size: 0.9rem;
  }
  
  .phone-frame {
    width: 200px;
    height: 400px;
  }
}
</style>
